import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  runTransaction,
  serverTimestamp,
  increment,
  writeBatch,
} from 'firebase/firestore';
import type { FragrancePartnerStock, FragranceStockTransaction, FragrancePartner, FragranceProduct, FragranceCode } from '@/types/fragrance';
import { db } from './firebase';

// Collection references
const partnerStockCollection = collection(db, 'fragrance_partner_stock');
const stockTransactionsCollection = collection(db, 'fragrance_stock_transactions');
const partnersCollection = collection(db, 'fragrance_partners');
const productsCollection = collection(db, 'fragrance_products');

/**
 * Get all partner stock records
 */
export async function getPartnerStocks() {
  const snapshot = await getDocs(query(partnerStockCollection, orderBy('partnerName'), orderBy('fragranceName')));
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FragrancePartnerStock));
}

/**
 * Get partner stock for a specific partner
 */
export async function getPartnerStock(partnerId: string) {
  const q = query(partnerStockCollection, where('partnerId', '==', partnerId), orderBy('fragranceName'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FragrancePartnerStock));
}

/**
 * Get stock for a specific partner and fragrance combination
 */
export async function getPartnerFragranceStock(partnerId: string, fragranceId: string): Promise<FragrancePartnerStock | null> {
  const q = query(
    partnerStockCollection,
    where('partnerId', '==', partnerId),
    where('fragranceId', '==', fragranceId)
  );
  const snapshot = await getDocs(q);
  
  if (snapshot.empty) {
    return null;
  }
  
  return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as FragrancePartnerStock;
}

/**
 * Set initial stock for a partner for a specific fragrance product
 */
export async function setPartnerInitialStock(
  partnerId: string,
  fragranceId: string,
  initialStock: number,
  adminUserId: string
): Promise<FragrancePartnerStock> {
  return await runTransaction(db, async (transaction) => {
    // Get partner details
    const partnerRef = doc(partnersCollection, partnerId);
    const partnerSnap = await transaction.get(partnerRef);
    
    if (!partnerSnap.exists()) {
      throw new Error('Partner not found');
    }
    
    const partnerData = partnerSnap.data() as FragrancePartner;
    
    // Get fragrance details
    const fragranceRef = doc(productsCollection, fragranceId);
    const fragranceSnap = await transaction.get(fragranceRef);
    
    if (!fragranceSnap.exists()) {
      throw new Error('Fragrance product not found');
    }
    
    const fragranceData = fragranceSnap.data() as FragranceProduct;
    
    // Check if stock record already exists
    const existingStockQuery = query(
      partnerStockCollection,
      where('partnerId', '==', partnerId),
      where('fragranceId', '==', fragranceId)
    );
    const existingStockSnap = await getDocs(existingStockQuery);
    
    let stockRecord: FragrancePartnerStock;
    let stockRef;
    
    if (!existingStockSnap.empty) {
      // Update existing stock record
      stockRef = existingStockSnap.docs[0].ref;
      const existingData = existingStockSnap.docs[0].data() as FragrancePartnerStock;
      
      stockRecord = {
        ...existingData,
        initialStock,
        currentStock: initialStock, // Reset current stock to new initial value
        soldCount: 0, // Reset counters
        returnedCount: 0,
        updatedAt: serverTimestamp() as any,
      };
      
      transaction.update(stockRef, stockRecord);
    } else {
      // Create new stock record
      stockRef = doc(partnerStockCollection);
      
      stockRecord = {
        partnerId,
        partnerName: partnerData.name,
        partnerCode: partnerData.code,
        fragranceId,
        fragranceName: fragranceData.name,
        currentStock: initialStock,
        initialStock,
        soldCount: 0,
        returnedCount: 0,
        lastUpdated: serverTimestamp() as any,
        createdAt: serverTimestamp() as any,
        updatedAt: serverTimestamp() as any,
      };
      
      transaction.set(stockRef, stockRecord);
    }
    
    // Create stock transaction record
    const transactionRef = doc(stockTransactionsCollection);
    const stockTransaction: FragranceStockTransaction = {
      partnerId,
      partnerCode: partnerData.code,
      fragranceId,
      type: 'initial_stock',
      quantityChange: initialStock,
      previousStock: existingStockSnap.empty ? 0 : (existingStockSnap.docs[0].data() as FragrancePartnerStock).currentStock,
      newStock: initialStock,
      reason: `Admin set initial stock to ${initialStock} units`,
      adminUserId,
      createdAt: serverTimestamp() as any,
    };
    
    transaction.set(transactionRef, stockTransaction);
    
    return { id: stockRef.id, ...stockRecord };
  });
}

/**
 * Update partner stock when a code is sold
 */
export async function updatePartnerStockOnSale(
  partnerId: string,
  fragranceId: string,
  fragranceCodeId: string,
  fragranceCode: string
): Promise<void> {
  await runTransaction(db, async (transaction) => {
    // Find the stock record
    const stockQuery = query(
      partnerStockCollection,
      where('partnerId', '==', partnerId),
      where('fragranceId', '==', fragranceId)
    );
    const stockSnap = await getDocs(stockQuery);
    
    if (stockSnap.empty) {
      console.warn(`No stock record found for partner ${partnerId} and fragrance ${fragranceId} - skipping stock update`);
      return;
    }
    
    const stockDoc = stockSnap.docs[0];
    const stockData = stockDoc.data() as FragrancePartnerStock;
    
    if (stockData.currentStock <= 0) {
      console.warn(`Partner ${stockData.partnerName} has no stock for ${stockData.fragranceName} but code was sold`);
      // Don't throw error, just log warning and continue
    }
    
    const newStock = Math.max(0, stockData.currentStock - 1);
    const updatedStockData = {
      currentStock: newStock,
      soldCount: increment(1),
      lastUpdated: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    
    transaction.update(stockDoc.ref, updatedStockData);
    
    // Create stock transaction record
    const transactionRef = doc(stockTransactionsCollection);
    const stockTransaction: FragranceStockTransaction = {
      partnerId,
      partnerCode: stockData.partnerCode,
      fragranceId,
      fragranceCodeId,
      fragranceCode,
      type: 'sold',
      quantityChange: -1,
      previousStock: stockData.currentStock,
      newStock,
      reason: `Code ${fragranceCode} sold`,
      createdAt: serverTimestamp() as any,
    };
    
    transaction.set(transactionRef, stockTransaction);
  });
}

/**
 * Update partner stock when a code is returned
 */
export async function updatePartnerStockOnReturn(
  partnerId: string,
  fragranceId: string,
  fragranceCodeId: string,
  fragranceCode: string
): Promise<void> {
  await runTransaction(db, async (transaction) => {
    // Find the stock record
    const stockQuery = query(
      partnerStockCollection,
      where('partnerId', '==', partnerId),
      where('fragranceId', '==', fragranceId)
    );
    const stockSnap = await getDocs(stockQuery);
    
    if (stockSnap.empty) {
      console.warn(`No stock record found for partner ${partnerId} and fragrance ${fragranceId} - skipping stock update`);
      return;
    }
    
    const stockDoc = stockSnap.docs[0];
    const stockData = stockDoc.data() as FragrancePartnerStock;
    
    const newStock = stockData.currentStock + 1;
    const updatedStockData = {
      currentStock: newStock,
      returnedCount: increment(1),
      soldCount: increment(-1), // Decrease sold count when item is returned
      lastUpdated: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    
    transaction.update(stockDoc.ref, updatedStockData);
    
    // Create stock transaction record
    const transactionRef = doc(stockTransactionsCollection);
    const stockTransaction: FragranceStockTransaction = {
      partnerId,
      partnerCode: stockData.partnerCode,
      fragranceId,
      fragranceCodeId,
      fragranceCode,
      type: 'returned',
      quantityChange: 1,
      previousStock: stockData.currentStock,
      newStock,
      reason: `Code ${fragranceCode} returned`,
      createdAt: serverTimestamp() as any,
    };
    
    transaction.set(transactionRef, stockTransaction);
  });
}

/**
 * Get stock transactions for a partner
 */
export async function getPartnerStockTransactions(partnerId: string) {
  const q = query(
    stockTransactionsCollection,
    where('partnerId', '==', partnerId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FragranceStockTransaction));
}

/**
 * Get all stock transactions
 */
export async function getAllStockTransactions() {
  const q = query(stockTransactionsCollection, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FragranceStockTransaction));
}

/**
 * Manual stock adjustment by admin
 */
export async function adjustPartnerStock(
  partnerId: string,
  fragranceId: string,
  adjustment: number,
  reason: string,
  adminUserId: string
): Promise<void> {
  await runTransaction(db, async (transaction) => {
    // Find the stock record
    const stockQuery = query(
      partnerStockCollection,
      where('partnerId', '==', partnerId),
      where('fragranceId', '==', fragranceId)
    );
    const stockSnap = await getDocs(stockQuery);
    
    if (stockSnap.empty) {
      throw new Error('Stock record not found');
    }
    
    const stockDoc = stockSnap.docs[0];
    const stockData = stockDoc.data() as FragrancePartnerStock;
    
    const newStock = Math.max(0, stockData.currentStock + adjustment);
    const updatedStockData = {
      currentStock: newStock,
      lastUpdated: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    
    transaction.update(stockDoc.ref, updatedStockData);
    
    // Create stock transaction record
    const transactionRef = doc(stockTransactionsCollection);
    const stockTransaction: FragranceStockTransaction = {
      partnerId,
      partnerCode: stockData.partnerCode,
      fragranceId,
      type: 'adjustment',
      quantityChange: adjustment,
      previousStock: stockData.currentStock,
      newStock,
      reason,
      adminUserId,
      createdAt: serverTimestamp() as any,
    };
    
    transaction.set(transactionRef, stockTransaction);
  });
}

/**
 * Enhanced debug function to investigate partner stock discrepancies with detailed saleSource analysis
 */
export async function debugPartnerStockCodes(partnerId: string, fragranceId: string): Promise<any> {
  try {
    console.log(`🔍 Debugging codes for partner ${partnerId} and fragrance ${fragranceId}`);
    
    // Get partner details
    const partnerDoc = await getDoc(doc(partnersCollection, partnerId));
    if (!partnerDoc.exists()) {
      throw new Error('Partner not found');
    }
    const partnerData = partnerDoc.data() as FragrancePartner;
    const partnerCode = partnerData.code;
    
    // Get fragrance details
    const fragranceDoc = await getDoc(doc(productsCollection, fragranceId));
    if (!fragranceDoc.exists()) {
      throw new Error('Fragrance not found');
    }
    const fragranceData = fragranceDoc.data() as FragranceProduct;
    
    console.log(`Partner: ${partnerData.name} (${partnerCode})`);
    console.log(`Product: ${fragranceData.name}`);
    
    // Get ALL codes for this partner-fragrance combination
    const codesQuery = query(
      collection(db, 'fragrance_codes'),
      where('partnerId', '==', partnerId),
      where('fragranceId', '==', fragranceId)
    );
    
    const codesSnapshot = await getDocs(codesQuery);
    const allCodes = codesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FragranceCode));
    
    console.log(`📊 Found ${allCodes.length} total codes for this partner-product combination`);
    
    // Detailed analysis of each code
    const statusBreakdown: any = {};
    const saleSourceBreakdown: any = {};
    let partnerSoldCodes = 0;
    let adminSoldCodes = 0;
    let onlineSoldCodes = 0;
    let codesWithNoSaleSource = 0;
    let codesWithWrongSaleSource = 0;
    
    // Detailed code analysis
    const detailedCodes = allCodes.map(code => {
      const status = code.status || 'unknown';
      statusBreakdown[status] = (statusBreakdown[status] || 0) + 1;
      
      const analysis: any = {
        code: code.code,
        status: code.status,
        isUsed: code.isUsed,
        saleSource: code.saleSource || 'null',
        soldAt: code.soldAt?.toDate()?.toISOString() || null,
        shouldCountAsPartnerSale: false,
        reason: ''
      };
      
      // Determine if this should count as partner sale
      if (code.isUsed && (code.status === 'sold' || code.status === 'redeemed')) {
        const saleSource = code.saleSource || 'unknown';
        saleSourceBreakdown[saleSource] = (saleSourceBreakdown[saleSource] || 0) + 1;
        
        if (saleSource === partnerCode) {
          partnerSoldCodes++;
          analysis.shouldCountAsPartnerSale = true;
          analysis.reason = `✅ Correctly counted as partner sale (saleSource: ${saleSource} matches partnerCode: ${partnerCode}) - Status: ${code.status}`;
        } else if (!saleSource || saleSource === 'null' || saleSource === 'unknown') {
          codesWithNoSaleSource++;
          analysis.reason = `❌ Missing saleSource - could be partner sale but can't verify - Status: ${code.status}`;
        } else if (saleSource === 'tel_u_asegh' || saleSource === 'admin') {
          adminSoldCodes++;
          analysis.reason = `ℹ️ Admin/online sale (saleSource: ${saleSource}) - Status: ${code.status}`;
        } else {
          codesWithWrongSaleSource++;
          onlineSoldCodes++;
          analysis.reason = `⚠️ Unknown saleSource: ${saleSource} (expected: ${partnerCode} for partner sales) - Status: ${code.status}`;
        }
      } else {
        analysis.reason = `ℹ️ Not sold (status: ${code.status}, isUsed: ${code.isUsed})`;
      }
      
      return analysis;
    });
    
    const report = {
      partnerName: partnerData.name,
      partnerCode,
      productName: fragranceData.name,
      totalCodes: allCodes.length,
      statusBreakdown,
      saleSourceBreakdown,
      salesSummary: {
        partnerSold: partnerSoldCodes,
        adminSold: adminSoldCodes,
        onlineSold: onlineSoldCodes,
        totalSold: partnerSoldCodes + adminSoldCodes + onlineSoldCodes,
        codesWithNoSaleSource,
        codesWithWrongSaleSource
      },
      detailedAnalysis: detailedCodes,
      recommendations: []
    };
    
    // Add recommendations based on analysis
    if (codesWithNoSaleSource > 0) {
      report.recommendations.push(`🔧 ${codesWithNoSaleSource} codes have no saleSource - these might be partner sales that need fixing`);
    }
    if (codesWithWrongSaleSource > 0) {
      report.recommendations.push(`🔧 ${codesWithWrongSaleSource} codes have unexpected saleSource values`);
    }
    if (partnerSoldCodes === 0 && (adminSoldCodes > 0 || onlineSoldCodes > 0)) {
      report.recommendations.push(`🔧 No partner sales detected but ${adminSoldCodes + onlineSoldCodes} codes are sold - check if partner sales are properly marked`);
    }
    
    console.log('📋 Enhanced Debug Report:', JSON.stringify(report, null, 2));
    
    // Also log detailed code analysis in a readable format
    console.log('\n📝 Detailed Code Analysis:');
    detailedCodes.forEach((codeAnalysis, index) => {
      console.log(`${index + 1}. Code: ${codeAnalysis.code}`);
      console.log(`   Status: ${codeAnalysis.status}, Used: ${codeAnalysis.isUsed}`);
      console.log(`   SaleSource: ${codeAnalysis.saleSource}`);
      console.log(`   ${codeAnalysis.reason}`);
      if (codeAnalysis.soldAt) {
        console.log(`   Sold At: ${codeAnalysis.soldAt}`);
      }
      console.log('');
    });
    
    return report;
    
  } catch (error) {
    console.error('Error debugging partner stock codes:', error);
    throw error;
  }
}

/**
 * Improved sync function with better debugging and more accurate counting
 */
export async function syncPartnerStockWithCodes(partnerId?: string): Promise<void> {
  try {
    console.log('🔄 Starting improved partner stock sync with actual code statuses...');
    
    // Get all stock records (or just for specific partner)
    let stockQuery = query(partnerStockCollection);
    if (partnerId) {
      stockQuery = query(partnerStockCollection, where('partnerId', '==', partnerId));
    }
    
    const stockSnapshot = await getDocs(stockQuery);
    
    for (const stockDoc of stockSnapshot.docs) {
      const stockData = stockDoc.data() as FragrancePartnerStock;
      
      console.log(`\n🔍 Syncing stock for ${stockData.partnerName} - ${stockData.fragranceName}`);
      
      // Get partner code
      const partnerDoc = await getDoc(doc(partnersCollection, stockData.partnerId));
      if (!partnerDoc.exists()) {
        console.error(`❌ Partner not found: ${stockData.partnerId}`);
        continue;
      }
      const partnerData = partnerDoc.data() as FragrancePartner;
      const partnerCode = partnerData.code;
      
      // Get all codes for this partner-product combination
      const codesQuery = query(
        collection(db, 'fragrance_codes'),
        where('partnerId', '==', stockData.partnerId),
        where('fragranceId', '==', stockData.fragranceId)
      );
      
      const codesSnapshot = await getDocs(codesQuery);
      const codes = codesSnapshot.docs.map(doc => doc.data() as FragranceCode);
      
      console.log(`   📦 Found ${codes.length} total codes for this combination`);
      
      // Count codes more accurately
      let soldByPartner = 0;
      let returnedByPartner = 0;
      let soldByOthers = 0;
      
      codes.forEach(code => {
        // Count as sold by partner if:
        // 1. Code is sold OR redeemed (both count as partner sales)
        // 2. saleSource matches partner code
        if (code.isUsed && (code.status === 'sold' || code.status === 'redeemed') && code.saleSource === partnerCode) {
          soldByPartner++;
        }
        // Count as sold by others (admin/online)
        else if (code.isUsed && (code.status === 'sold' || code.status === 'redeemed') && code.saleSource !== partnerCode) {
          soldByOthers++;
        }
        // Count as returned if code was previously sold by partner but is now available
        else if (!code.isUsed && code.status === 'available' && code.saleSource === partnerCode) {
          returnedByPartner++;
        }
      });
      
      // Calculate what the current stock should be
      const calculatedCurrentStock = stockData.initialStock - soldByPartner + returnedByPartner;
      
      console.log(`   📊 Analysis:`);
      console.log(`     Initial stock: ${stockData.initialStock}`);
      console.log(`     Sold by partner (${partnerCode}): ${soldByPartner}`);
      console.log(`     Sold by others: ${soldByOthers}`);
      console.log(`     Returned by partner: ${returnedByPartner}`);
      console.log(`     Current stock in DB: ${stockData.currentStock}`);
      console.log(`     Calculated current stock: ${calculatedCurrentStock}`);
      console.log(`     Current soldCount in DB: ${stockData.soldCount}`);
      console.log(`     Current returnedCount in DB: ${stockData.returnedCount}`);
      
      // Update the stock record if there's a discrepancy
      if (stockData.soldCount !== soldByPartner || 
          stockData.returnedCount !== returnedByPartner || 
          stockData.currentStock !== Math.max(0, calculatedCurrentStock)) {
        
        console.log(`   ✏️  Updating stock record to fix discrepancies`);
        
        await updateDoc(stockDoc.ref, {
          soldCount: soldByPartner,
          returnedCount: returnedByPartner,
          currentStock: Math.max(0, calculatedCurrentStock),
          lastUpdated: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        
        // Create a transaction record for this sync
        const transactionRef = doc(stockTransactionsCollection);
        const stockTransaction: FragranceStockTransaction = {
          partnerId: stockData.partnerId,
          partnerCode: stockData.partnerCode,
          fragranceId: stockData.fragranceId,
          type: 'adjustment',
          quantityChange: Math.max(0, calculatedCurrentStock) - stockData.currentStock,
          previousStock: stockData.currentStock,
          newStock: Math.max(0, calculatedCurrentStock),
          reason: `Stock sync: soldCount ${stockData.soldCount}→${soldByPartner}, returnedCount ${stockData.returnedCount}→${returnedByPartner}`,
          adminUserId: 'system-sync',
          createdAt: serverTimestamp() as any,
        };
        
        await addDoc(stockTransactionsCollection, stockTransaction);
        
        console.log(`   ✅ Stock record updated successfully`);
      } else {
        console.log(`   ✅ Stock record is already in sync`);
      }
    }
    
    console.log('🎉 Partner stock sync completed successfully');
  } catch (error) {
    console.error('❌ Error syncing partner stock with codes:', error);
    throw error;
  }
}

/**
 * Initialize stock records for all existing partner-product combinations
 */
export async function initializeAllPartnerStocks(): Promise<void> {
  const partnersSnapshot = await getDocs(partnersCollection);
  const productsSnapshot = await getDocs(productsCollection);
  
  const batch = writeBatch(db);
  let batchCount = 0;
  
  for (const partnerDoc of partnersSnapshot.docs) {
    const partnerData = partnerDoc.data() as FragrancePartner;
    
    for (const productDoc of productsSnapshot.docs) {
      const productData = productDoc.data() as FragranceProduct;
      
      // Check if stock record already exists
      const existingStockQuery = query(
        partnerStockCollection,
        where('partnerId', '==', partnerDoc.id),
        where('fragranceId', '==', productDoc.id)
      );
      const existingStockSnap = await getDocs(existingStockQuery);
      
      if (existingStockSnap.empty) {
        // Create new stock record with 0 initial stock
        const stockRef = doc(partnerStockCollection);
        const stockRecord: FragrancePartnerStock = {
          partnerId: partnerDoc.id,
          partnerName: partnerData.name,
          partnerCode: partnerData.code,
          fragranceId: productDoc.id,
          fragranceName: productData.name,
          currentStock: 0,
          initialStock: 0,
          soldCount: 0,
          returnedCount: 0,
          lastUpdated: serverTimestamp() as any,
          createdAt: serverTimestamp() as any,
          updatedAt: serverTimestamp() as any,
        };
        
        batch.set(stockRef, stockRecord);
        batchCount++;
        
        // Commit batch if it gets too large
        if (batchCount >= 500) {
          await batch.commit();
          batchCount = 0;
        }
      }
    }
  }
  
  // Commit remaining operations
  if (batchCount > 0) {
    await batch.commit();
  }
}

/**
 * Fix saleSource fields for codes that were sold by partners but have incorrect or missing saleSource
 * This is useful when partner sales weren't properly tracked in the past
 */
export async function fixPartnerSaleSource(partnerId: string, fragranceId: string): Promise<void> {
  try {
    console.log(`🔧 Fixing saleSource for partner ${partnerId} and fragrance ${fragranceId}`);
    
    // Get partner details
    const partnerDoc = await getDoc(doc(partnersCollection, partnerId));
    if (!partnerDoc.exists()) {
      throw new Error('Partner not found');
    }
    const partnerData = partnerDoc.data() as FragrancePartner;
    const partnerCode = partnerData.code;
    
    // Get fragrance details
    const fragranceDoc = await getDoc(doc(productsCollection, fragranceId));
    if (!fragranceDoc.exists()) {
      throw new Error('Fragrance not found');
    }
    const fragranceData = fragranceDoc.data() as FragranceProduct;
    
    console.log(`Partner: ${partnerData.name} (${partnerCode})`);
    console.log(`Product: ${fragranceData.name}`);
    
    // Get codes that are sold OR redeemed but have wrong/missing saleSource
    const codesQuery = query(
      collection(db, 'fragrance_codes'),
      where('partnerId', '==', partnerId),
      where('fragranceId', '==', fragranceId),
      where('isUsed', '==', true)
    );
    
    const codesSnapshot = await getDocs(codesQuery);
    const soldCodes = codesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FragranceCode));
    
    // Filter to only sold and redeemed codes
    const relevantCodes = soldCodes.filter(code => 
      code.status === 'sold' || code.status === 'redeemed'
    );
    
    console.log(`Found ${relevantCodes.length} sold/redeemed codes for this partner-product combination`);
    
    let fixedCount = 0;
    const batch = writeBatch(db);
    
    relevantCodes.forEach(code => {
      const currentSaleSource = code.saleSource;
      
      // Fix codes that have wrong or missing saleSource
      // We assume codes assigned to this partner and marked as sold were sold by the partner
      // unless they explicitly have 'tel_u_asegh' or 'admin' as saleSource
      if (
        !currentSaleSource || 
        currentSaleSource === 'unknown' || 
        currentSaleSource === 'null' ||
        (currentSaleSource !== partnerCode && currentSaleSource !== 'tel_u_asegh' && currentSaleSource !== 'admin')
      ) {
        console.log(`🔧 Fixing code ${code.code}: saleSource "${currentSaleSource}" → "${partnerCode}"`);
        
        const codeRef = doc(collection(db, 'fragrance_codes'), code.id!);
        batch.update(codeRef, {
          saleSource: partnerCode,
          updatedAt: serverTimestamp()
        });
        
        fixedCount++;
      }
    });
    
    if (fixedCount > 0) {
      await batch.commit();
      console.log(`✅ Fixed saleSource for ${fixedCount} codes`);
      
      // Create a stock transaction record for this fix
      const transactionRef = doc(stockTransactionsCollection);
      const stockTransaction: FragranceStockTransaction = {
        partnerId,
        partnerCode,
        fragranceId,
        type: 'adjustment',
        quantityChange: 0, // No stock change, just fixing tracking
        previousStock: 0,
        newStock: 0,
        reason: `Fixed saleSource for ${fixedCount} codes from various values to "${partnerCode}"`,
        adminUserId: 'system-fix',
        createdAt: serverTimestamp() as any,
      };
      
      await addDoc(stockTransactionsCollection, stockTransaction);
    } else {
      console.log(`✅ No codes needed saleSource fixing`);
    }
    
  } catch (error) {
    console.error('Error fixing partner saleSource:', error);
    throw error;
  }
}

/**
 * Fix saleSource for all partner-product combinations
 */
export async function fixAllPartnerSaleSources(): Promise<void> {
  try {
    console.log('🔧 Starting bulk saleSource fix for all partners...');
    
    // Get all stock records
    const stockSnapshot = await getDocs(partnerStockCollection);
    
    for (const stockDoc of stockSnapshot.docs) {
      const stockData = stockDoc.data() as FragrancePartnerStock;
      console.log(`\n🔧 Processing ${stockData.partnerName} - ${stockData.fragranceName}`);
      
      try {
        await fixPartnerSaleSource(stockData.partnerId, stockData.fragranceId);
      } catch (error) {
        console.error(`Error fixing saleSource for ${stockData.partnerName} - ${stockData.fragranceName}:`, error);
        // Continue with other combinations
      }
    }
    
    console.log('🎉 Bulk saleSource fix completed');
  } catch (error) {
    console.error('Error in bulk saleSource fix:', error);
    throw error;
  }
}

/**
 * Comprehensive auto-fix function that resolves all stock tracking issues
 * This is a one-click solution that:
 * 1. Fixes missing/incorrect saleSource fields
 * 2. Syncs stock counts with actual code statuses  
 * 3. Handles both sold and redeemed codes
 * 4. Creates proper audit trail
 */
export async function autoFixAllStockIssues(): Promise<void> {
  try {
    console.log('🚀 Starting comprehensive stock auto-fix...');
    
    // Step 1: Initialize stock records for any missing partner-product combinations
    console.log('📦 Step 1: Ensuring all partner stock records exist...');
    await initializeAllPartnerStocks();
    
    // Step 2: Fix saleSource issues for all partners
    console.log('🔧 Step 2: Fixing saleSource fields for all partners...');
    await fixAllPartnerSaleSources();
    
    // Step 3: Sync all stock counts with actual code statuses
    console.log('🔄 Step 3: Syncing stock counts with actual sales...');
    await syncPartnerStockWithCodes();
    
    console.log('✅ Stock auto-fix completed successfully!');
    
  } catch (error) {
    console.error('❌ Error in comprehensive stock auto-fix:', error);
    throw error;
  }
}
